import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { NavLink, useLocation } from "react-router";
import ReportRepository from "@/repositories/report.repo";
import CardIcon from "@/components/icons/card.icon";
import ChefIcon from "@/components/icons/chef.icon";
import DashboardIcon from "@/components/icons/dashboard.icon";
import HotelIcon from "@/components/icons/hotel.icon";
import InvoiceIcon from "@/components/icons/invoice.icon";
import ReservationIcon from "@/components/icons/reservation.icon";
import RoomIcon from "@/components/icons/room.icon";
import SecurityLockIcon from "@/components/icons/security-lock.icon";
import SubsriptionIcon from "@/components/icons/subscription.icon";
import TerminalIcon from "@/components/icons/terminal.icon";
import UserGroupIcon from "@/components/icons/user-group.icon";
import UserLockIcon from "@/components/icons/user-lock.icon";
import useAuth from "@/hooks/auth.hook";
import usePlanFeatures from "@/hooks/plan.hook";
import { useAppSelector } from "@/store/hooks";
import { TITLE_TO_MODULE } from "@/utils/modules";

const adminMenuItems = [
  {
    title: "Dashboard",
    path: "/dashboard",
    end: true,
    icon: <DashboardIcon className="size-4" />,
  },
  { title: "Daily Operations", roles: ["*"] },
  {
    title: "Reservations",
    path: "/dashboard/reservations",
    setup: "hotel",
    icon: <ReservationIcon className="size-4" />,
  },
  {
    title: "Orders",
    path: "/dashboard/orders",
    setup: "restaurant",
    icon: <ChefIcon className="size-4" />,
  },
  {
    title: "Kitchen Display",
    path: "/dashboard/kitchen",
    setup: "restaurant",
    icon: <ChefIcon className="size-4" />,
  },
  {
    title: "Customers",
    path: "/dashboard/customers",
    icon: <UserGroupIcon className="size-4" />,
  },
  {
    title: "Inventory",
    path: "/dashboard/inventory/overview",
    activePrefix: "/dashboard/inventory",
    setup: "operations",
    icon: <RoomIcon className="size-4" />,
  },
  { title: "Reports", roles: ["*"], is_hotel: true, is_restaurant: true },
  {
    title: "Invoices",
    path: "/dashboard/invoices",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Accounting",
    feature: "accounting",
    path: "/dashboard/accounting",
    icon: <CardIcon className="size-4" />,
  },
  {
    title: "Insights",
    feature: "insights",
    path: "/dashboard/insights",
    icon: <DashboardIcon className="size-4" />,
  },
  {
    title: "VAT Report",
    path: "/dashboard/vat-report",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Standard View",
    path: "/dashboard/standard-view",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Activity",
    path: "/dashboard/activity",
    icon: <TerminalIcon className="size-4" />,
  },
  {
    title: "Transactions",
    path: "/dashboard/transactions",
    icon: <InvoiceIcon className="size-4" />,
  },
  { title: "Setup", roles: ["*"], is_hotel: true, is_restaurant: true },
  {
    title: "Staffs",
    path: "/dashboard/staffs",
    icon: <UserLockIcon className="size-4" />,
  },
  {
    title: "Roles",
    path: "/dashboard/roles",
    icon: <SecurityLockIcon className="size-4" />,
  },
  {
    title: "Business",
    path: "/dashboard/business",
    icon: <RoomIcon className="size-4" />,
  },
  {
    title: "Subscription",
    path: "/dashboard/subscription",
    icon: <CardIcon className="size-4" />,
  },
  { title: "Management", roles: ["super-admin"] },
  {
    title: "Business",
    path: "/dashboard/manage/business",
    icon: <HotelIcon className="size-4" />,
  },
  {
    title: "Users",
    path: "/dashboard/manage/users",
    icon: <UserLockIcon className="size-4" />,
  },
  {
    title: "Subscriptions",
    path: "/dashboard/manage/subscriptions",
    icon: <SubsriptionIcon className="size-4" />,
  },
  {
    title: "Promo Codes",
    path: "/dashboard/manage/promos",
    icon: <SubsriptionIcon className="size-4" />,
  },
  {
    title: "Travel Agencies",
    path: "/dashboard/manage/travel-agencies",
    icon: <DashboardIcon className="size-4" />,
  },
]

const hotelMenuItems = [
  {
    title: "Dashboard",
    path: "/dashboard",
    end: true,
    icon: <DashboardIcon className="size-4" />,
  },
  { title: "Daily Operations", roles: ["*"] },
  {
    title: "Reservations",
    path: "/dashboard/reservations",
    setup: "hotel",
    icon: <ReservationIcon className="size-4" />,
  },
  {
    title: "Orders",
    path: "/dashboard/orders",
    setup: "restaurant",
    icon: <ChefIcon className="size-4" />,
  },
  {
    title: "Kitchen Display",
    path: "/dashboard/kitchen",
    setup: "restaurant",
    icon: <ChefIcon className="size-4" />,
  },
  {
    title: "Customers",
    path: "/dashboard/customers",
    icon: <UserGroupIcon className="size-4" />,
  },
  {
    title: "Inventory",
    path: "/dashboard/inventory/overview",
    activePrefix: "/dashboard/inventory",
    setup: "operations",
    icon: <RoomIcon className="size-4" />,
  },
  { title: "Reports" },
  {
    title: "Invoices",
    path: "/dashboard/invoices",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Accounting",
    feature: "accounting",
    path: "/dashboard/accounting",
    icon: <CardIcon className="size-4" />,
  },
  {
    title: "Insights",
    feature: "insights",
    path: "/dashboard/insights",
    icon: <DashboardIcon className="size-4" />,
  },
  {
    title: "VAT Report",
    path: "/dashboard/vat-report",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Standard View",
    path: "/dashboard/standard-view",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Activity",
    path: "/dashboard/activity",
    icon: <TerminalIcon className="size-4" />,
  },
  { title: "Setup" },
  {
    title: "Staffs",
    path: "/dashboard/staffs",
    icon: <UserLockIcon className="size-4" />,
  },
  {
    title: "Roles",
    path: "/dashboard/roles",
    icon: <SecurityLockIcon className="size-4" />,
  },
  {
    title: "Business",
    path: "/dashboard/business",
    icon: <RoomIcon className="size-4" />,
  },
  {
    title: "Subscription",
    path: "/dashboard/subscription",
    icon: <CardIcon className="size-4" />,
  },
  { title: "Management", roles: ["super-admin"] },
  {
    title: "Business",
    path: "/dashboard/manage/business",
    icon: <HotelIcon className="size-4" />,
  },
  {
    title: "Users",
    path: "/dashboard/manage/users",
    icon: <UserLockIcon className="size-4" />,
  },
  {
    title: "Subscriptions",
    path: "/dashboard/manage/subscriptions",
    icon: <SubsriptionIcon className="size-4" />,
  },
  {
    title: "Promo Codes",
    path: "/dashboard/manage/promos",
    icon: <SubsriptionIcon className="size-4" />,
  },
  {
    title: "Travel Agencies",
    path: "/dashboard/manage/travel-agencies",
    icon: <DashboardIcon className="size-4" />,
  },
]
const restaurantMenuItems = [
  {
    title: "Dashboard",
    path: "/dashboard",
    end: true,
    icon: <DashboardIcon className="size-4" />,
  },
  { title: "Daily Operations" },
  {
    title: "Reservations",
    path: "/dashboard/reservations",
    setup: "hotel",
    icon: <ReservationIcon className="size-4" />,
  },
  {
    title: "Orders",
    path: "/dashboard/orders",
    setup: "restaurant",
    icon: <ChefIcon className="size-4" />,
  },
  {
    title: "Kitchen Display",
    path: "/dashboard/kitchen",
    setup: "restaurant",
    icon: <ChefIcon className="size-4" />,
  },
  {
    title: "Customers",
    path: "/dashboard/customers",
    icon: <UserGroupIcon className="size-4" />,
  },
  {
    title: "Inventory",
    path: "/dashboard/inventory/overview",
    activePrefix: "/dashboard/inventory",
    setup: "operations",
    icon: <RoomIcon className="size-4" />,
  },
  { title: "Reports", roles: ["*"], is_hotel: true, is_restaurant: true },
  {
    title: "Invoices",
    path: "/dashboard/invoices",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Accounting",
    feature: "accounting",
    path: "/dashboard/accounting",
    icon: <CardIcon className="size-4" />,
  },
  {
    title: "Insights",
    feature: "insights",
    path: "/dashboard/insights",
    icon: <DashboardIcon className="size-4" />,
  },
  {
    title: "VAT Report",
    path: "/dashboard/vat-report",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Standard View",
    path: "/dashboard/standard-view",
    icon: <InvoiceIcon className="size-4" />,
  },
  {
    title: "Activity",
    path: "/dashboard/activity",
    icon: <TerminalIcon className="size-4" />,
  },
  { title: "Setup", roles: ["*"], is_hotel: true, is_restaurant: true },
  {
    title: "Staffs",
    path: "/dashboard/staffs",
    icon: <UserLockIcon className="size-4" />,
  },
  {
    title: "Roles",
    path: "/dashboard/roles",
    icon: <SecurityLockIcon className="size-4" />,
  },
  {
    title: "Business",
    path: "/dashboard/business",
    icon: <RoomIcon className="size-4" />,
  },
  {
    title: "Subscription",
    path: "/dashboard/subscription",
    icon: <CardIcon className="size-4" />,
  },
  { title: "Management", roles: ["super-admin"] },
  {
    title: "Business",
    path: "/dashboard/manage/business",
    icon: <HotelIcon className="size-4" />,
  },
  {
    title: "Users",
    path: "/dashboard/manage/users",
    icon: <UserLockIcon className="size-4" />,
  },
  {
    title: "Subscriptions",
    path: "/dashboard/manage/subscriptions",
    icon: <SubsriptionIcon className="size-4" />,
  },
  {
    title: "Promo Codes",
    path: "/dashboard/manage/promos",
    icon: <SubsriptionIcon className="size-4" />,
  },
  {
    title: "Travel Agencies",
    path: "/dashboard/manage/travel-agencies",
    icon: <DashboardIcon className="size-4" />,
  },
]

// Menu titles that remain available to a disabled/blocked business.
const INACTIVE_ALLOWED = ["Dashboard", "Business", "Subscription"];

// Reserved for super-admins: the "Management" section header (roles) and every /dashboard/manage/*
// route. Non-super users (business owners, staff) must never see these.
const isSuperAdminOnly = (item: any): boolean =>
  (Array.isArray(item?.roles) && item.roles.includes("super-admin")) ||
  (typeof item?.path === "string" && item.path.startsWith("/dashboard/manage"));

const Sidebar = () => {
  const { hasHotelAccess, hasRestaurantAccess, isSuperAdmin } = useAuth();
  const { hasFeature, usage, isModuleVisible } = usePlanFeatures();

  // Surface operational menus only for what the business has actually set up,
  // not merely what the subscription permits — e.g. a travel-only business with
  // a hotel plan but no hotels created shouldn't see Reservations/Orders/Kitchen.
  const hasHotels = isSuperAdmin || (usage?.hotels ?? 0) > 0;
  const hasRestaurants = isSuperAdmin || (usage?.restaurants ?? 0) > 0;
  const setupOk = (item: any) => {
    switch (item?.setup) {
      case "hotel":
        return hasHotels;
      case "restaurant":
        return hasRestaurants;
      case "operations":
        return hasHotels || hasRestaurants;
      default:
        return true;
    }
  };
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();

  // When an admin has disabled/blocked the business, collapse the sidebar to a minimal,
  // safe set so the owner can still see their account and manage their subscription.
  const businessActive = isSuperAdmin || !user?.business?.status || user.business.status === "active";

  // Live count of guest-submitted online requests; shares the home dashboard's query cache.
  const reportRepository = new ReportRepository();
  const { data: onlineRequests } = useQuery({
    queryKey: ["online-requests"],
    queryFn: async () => await reportRepository.onlineRequests(),
    enabled: !isSuperAdmin && businessActive,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const ordersBadge = onlineRequests?.orders?.length ?? 0;
  const bookingsBadge = onlineRequests?.bookings?.length ?? 0;

  const activeClass =
    "text-white transition-all active:scale-90 duration-300 font-[300] mx-4 px-3 rounded-3xl shadow-md py-2 bg-teal-900 flex items-center space-x-2 relative group is-active dark:bg-opacity-10 dark:text-slate-300";
  const inactiveClass =
    "text-white transition-all active:scale-90 duration-300 font-[300] mx-4 px-3 rounded-3xl py-2 hover:bg-teal-900 dark:hover:bg-opacity-10 flex items-center space-x-2 relative dark:text-slate-300";

  const Navigation = useCallback(
    ({ item }: any) => {
      if (item.path) {
        // `activePrefix` keeps a collapsed menu (e.g. Inventory) highlighted across all its sub-routes.
        const prefixActive = item.activePrefix ? location.pathname.startsWith(item.activePrefix) : false;
        const badge = item.title === "Orders" ? ordersBadge : item.title === "Reservations" ? bookingsBadge : 0;
        return (
          <NavLink
            to={item.path}
            key={item.title}
            end={item.end}
            className={({ isActive }) => (isActive || prefixActive ? activeClass : inactiveClass)}
          >
            <span>{item.icon}</span>
            <span>{item.title}</span>
            {badge > 0 && (
              <span
                title={`${badge} new online request${badge > 1 ? "s" : ""}`}
                className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-teal-950"
              >
                {badge}
              </span>
            )}
          </NavLink>
        );
      }
      return <span className="mx-4 px-3 font-[700] text-teal-900 text-xs pt-4 pb-1">{item.title}</span>;
    },
    [location.pathname, ordersBadge, bookingsBadge],
  );

  const RoleWiseMenu = useCallback(() => {
    if (isSuperAdmin) {
      return (
        <>
          {adminMenuItems.map((item, index) => <Navigation key={item?.path ?? `sec-${index}`} item={item} />)}
        </>
      );
    }

    // Everyone who is not a super-admin (business owners AND staff): operational menu only.
    // The Management section (Users, Roles, Subscriptions, Promo Codes, Travel Agencies) is never
    // rendered here — it is super-admin only.
    const menuToRender: any = [];
    if (hasHotelAccess) {
      menuToRender.push(...hotelMenuItems);
    }
    if (hasRestaurantAccess) {
      menuToRender.push(...restaurantMenuItems);
    }
    // Base menu (shared items like Dashboard/Customers/Business) for users with no
    // hotel/restaurant subscription — e.g. travel-only businesses — so the sidebar
    // is never blank. Operational items get filtered out by setupOk below.
    if (menuToRender.length === 0) {
      menuToRender.push(...hotelMenuItems);
    }
    // Remove duplicates
    const uniqueMenu = Array.from(new Set(menuToRender.map((item: any) => item.title))).map((title) => {
      return menuToRender.find((item: any) => item.title === title);
    });
    // Exclude super-admin-only items (Management section), hide feature-gated items (Accounting/
    // Insights) the plan doesn't include, operational items the business hasn't set up (no hotels →
    // no Reservations), and any module a super-admin has explicitly hidden for this business.
    let visibleMenu = uniqueMenu.filter(
      (item: any) =>
        !isSuperAdminOnly(item) &&
        hasFeature(item?.feature) &&
        setupOk(item) &&
        isModuleVisible(TITLE_TO_MODULE[item?.title]),
    );
    // A disabled/blocked business sees only the minimal account set.
    if (!businessActive) {
      visibleMenu = visibleMenu.filter((item: any) => INACTIVE_ALLOWED.includes(item?.title));
    }
    return visibleMenu.map((item: any, index: number) => <Navigation key={item?.path ?? `sec-${index}`} item={item} />);
  }, [hasHotelAccess, hasRestaurantAccess, isSuperAdmin, Navigation, hasFeature, isModuleVisible, hasHotels, hasRestaurants, businessActive]);

  return (
    <aside className="w-[220px] flex-none border-r-2 bg-teal-900/50 animate-background dark:border-slate-700 border-r-teal-900 flex flex-col">
      <div className="flex-none flex flex-col items-center justify-center p-4">
        {/* <span className="logo-stroke-small">B.</span> */}
        <img src="/basisthan-long.png" alt="logo" className="py-1.5 px-4" />
      </div>
      <PerfectScrollbar className="py-4 flex-grow flex flex-col">
        <div className="flex flex-col space-y-1 text-sm">
          <RoleWiseMenu />
          {/* {menuItems.map((item) => (
            <Navigation key={item} item={item} />
          ))} */}
        </div>
      </PerfectScrollbar>
      <div className="flex-none flex flex-col space-y-1 pb-4">
        <div className="min-h-[80px] bg-teal-900 text-white flex flex-col items-center justify-center mx-4 rounded-lg py-2">
          <span className="text-sm font-semibold">{user?.name}</span>
          <span className="text-[10px] mt-2 text-white flex items-center">
            <TerminalIcon className="size-3 text-white mr-1" />
            Version 2.0.0
          </span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
